/**
 * V6.8权限值对象 - 核心层（框架无关）
 * 实现 {industry_code}:{role}:{module}:{action} 格式的权限标识
 */

export class V6Permission {
  constructor(
    public readonly industryCode: string,
    public readonly role: string,
    public readonly module: string,
    public readonly action: string
  ) {
    this.validate()
  }

  /**
   * 从权限字符串解析创建权限对象
   */
  static fromString(permissionString: string): V6Permission {
    const parts = permissionString.split(':')
    
    if (parts.length !== 4) {
      throw new Error(`无效的权限格式: ${permissionString}`)
    }
    
    return new V6Permission(parts[0], parts[1], parts[2], parts[3])
  }

  /**
   * 转换为权限字符串
   */
  toString(): string {
    return `${this.industryCode}:${this.role}:${this.module}:${this.action}`
  }

  /**
   * 验证权限格式
   */
  private validate(): void {
    // 验证行业代码
    if (!this.isValidIndustryCode(this.industryCode)) {
      throw new Error(`无效的行业代码: ${this.industryCode}`)
    }

    // 验证角色
    if (!this.isValidRole(this.role)) {
      throw new Error(`无效的角色: ${this.role}`)
    }

    // 验证模块
    if (!this.isValidModule(this.module)) {
      throw new Error(`无效的模块: ${this.module}`)
    }

    // 验证操作
    if (!this.isValidAction(this.action)) {
      throw new Error(`无效的操作: ${this.action}`)
    }
  }

  /**
   * 检查行业代码是否有效
   */
  private isValidIndustryCode(code: string): boolean {
    const validCodes = ['AGR001', 'AUT001', 'EST001', 'EDU001', '*']
    return validCodes.includes(code)
  }

  /**
   * 检查角色是否有效
   */
  private isValidRole(role: string): boolean {
    const validRoles = [
      // 平台域
      'admin', 'operator',
      // 服务商域
      'vendor_owner', 'vendor_partner', 'vendor_expert',
      // 企业域
      'business_owner'
    ]
    return validRoles.includes(role)
  }

  /**
   * 检查模块是否有效
   */
  private isValidModule(module: string): boolean {
    const validModules = [
      // 核心模块
      'content', 'template', 'task', 'quality', 'distribution',
      'analysis', 'statistics', 'report', 'dashboard',
      // 企业主模块
      'brand', 'reception', 'knowledge', 'trust', 'tools', 'bill', 'settings', 'help', 'share',
      // 服务商模块
      'geo', 'business', 'team', 'tech', 'promotion', 'analytics', 'billing',
      // 通用
      '*'
    ]
    return validModules.includes(module)
  }

  /**
   * 检查操作是否有效
   */
  private isValidAction(action: string): boolean {
    const validActions = ['view', 'create', 'edit', 'delete', 'publish', 'review', 'export', 'execute', '*']
    return validActions.includes(action)
  }

  /**
   * 检查权限是否匹配（支持通配符）
   */
  matches(userIndustryCode: string, userRole: string): boolean {
    // 检查行业匹配
    const industryMatch = this.industryCode === '*' || this.industryCode === userIndustryCode
    
    // 检查角色匹配
    const roleMatch = this.role === userRole
    
    return industryMatch && roleMatch
  }

  /**
   * 检查是否为管理员权限
   */
  isAdminPermission(): boolean {
    return this.role === 'admin' && this.industryCode === '*'
  }

  /**
   * 检查权限是否相等
   */
  equals(other: V6Permission): boolean {
    return this.toString() === other.toString()
  }

  /**
   * 检查权限是否包含另一个权限（支持通配符）
   */
  contains(other: V6Permission): boolean {
    const industryMatch = this.industryCode === '*' || this.industryCode === other.industryCode
    const roleMatch = this.role === other.role
    const moduleMatch = this.module === '*' || this.module === other.module
    const actionMatch = this.action === '*' || this.action === other.action
    
    return industryMatch && roleMatch && moduleMatch && actionMatch
  }

  /**
   * 创建权限构建器
   */
  static builder() {
    return new PermissionBuilder()
  }
}

/**
 * 权限构建器 - 流式API设计
 */
class PermissionBuilder {
  private industryCode: string = '*' // 默认所有行业
  private role: string = 'operator' // 默认平台运营角色
  private module: string = ''
  private action: string = ''

  forIndustry(code: string): this {
    this.industryCode = code
    return this
  }

  forRole(role: string): this {
    this.role = role
    return this
  }

  forModule(module: string): this {
    this.module = module
    return this
  }

  forAction(action: string): this {
    this.action = action
    return this
  }

  build(): V6Permission {
    return new V6Permission(this.industryCode, this.role, this.module, this.action)
  }
}

/**
 * 预定义的权限常量
 */
export const ContentPermissions = {
  // 通用内容权限
  VIEW_CONTENT: V6Permission.builder().forModule('content').forAction('view').build(),
  CREATE_CONTENT: V6Permission.builder().forModule('content').forAction('create').build(),
  EDIT_CONTENT: V6Permission.builder().forModule('content').forAction('edit').build(),
  DELETE_CONTENT: V6Permission.builder().forModule('content').forAction('delete').build(),
  PUBLISH_CONTENT: V6Permission.builder().forModule('content').forAction('publish').build(),

  // 模板权限
  VIEW_TEMPLATE: V6Permission.builder().forModule('template').forAction('view').build(),
  CREATE_TEMPLATE: V6Permission.builder().forModule('template').forAction('create').build(),
  EDIT_TEMPLATE: V6Permission.builder().forModule('template').forAction('edit').build(),

  // 任务权限
  VIEW_TASK: V6Permission.builder().forModule('task').forAction('view').build(),
  CREATE_TASK: V6Permission.builder().forModule('task').forAction('create').build(),
  EXECUTE_TASK: V6Permission.builder().forModule('task').forAction('execute').build(),

  // 分析权限
  VIEW_ANALYSIS: V6Permission.builder().forModule('analysis').forAction('view').build(),
  EXPORT_REPORT: V6Permission.builder().forModule('report').forAction('export').build(),

  // 管理员权限
  ADMIN_ALL: V6Permission.builder().forIndustry('*').forRole('admin').forModule('*').forAction('*').build()
}

/**
 * 权限组 - 按角色分类
 */
export const PermissionGroups = {
  // 平台管理员 - 所有权限
  PLATFORM_ADMIN: Object.values(ContentPermissions),

  // 内容运营 - 内容管理权限
  PLATFORM_OPERATOR: [
    ContentPermissions.VIEW_CONTENT,
    ContentPermissions.CREATE_CONTENT,
    ContentPermissions.EDIT_CONTENT,
    ContentPermissions.PUBLISH_CONTENT,
    ContentPermissions.VIEW_TEMPLATE,
    ContentPermissions.VIEW_TASK
  ],

  // 平台专家 - 创作权限
  PLATFORM_EXPERT: [
    ContentPermissions.CREATE_CONTENT,
    ContentPermissions.EDIT_CONTENT,
    ContentPermissions.VIEW_TEMPLATE,
    ContentPermissions.CREATE_TASK,
    ContentPermissions.EXECUTE_TASK
  ],

  // 业务主 - 企业内容管理权限
  BUSINESS_OWNER: [
    ContentPermissions.VIEW_CONTENT,
    ContentPermissions.CREATE_CONTENT,
    ContentPermissions.EDIT_CONTENT,
    ContentPermissions.PUBLISH_CONTENT,
    ContentPermissions.VIEW_TASK
  ],

  // 企业成员 - 基础内容权限
  BUSINESS_MEMBER: [
    ContentPermissions.VIEW_CONTENT,
    ContentPermissions.CREATE_CONTENT,
    ContentPermissions.VIEW_TASK
  ]
}